import React, { useContext, useMemo, useState, useEffect } from "react";
import { Card, Spinner } from "react-bootstrap";
import { Context } from "../../index";
import { FaHourglassHalf, FaClock } from "react-icons/fa";

// helpers
const nowMin = () => Math.floor(Date.now() / 60000);

const fmtTimeLeft = (targetMin, kind /* "start" | "end" */) => {
    if (!targetMin && targetMin !== 0) return "—";
    const left = targetMin - nowMin();
    const sign = left <= 0 ? 0 : left;
    const d = Math.floor(sign / 1440);
    const h = Math.floor((sign % 1440) / 60);
    const tag = kind === "start" ? "Starts in" : "Ends in";
    if (left <= 0) return kind === "start" ? "Started" : "Ended";
    if (d > 0) return `${tag} ${d}d ${h}h`;
    if (h > 0) return `${tag} ${h}h`;
    return `${tag} <1h`;
};

// сборка данных из Context.book
const useOffers = (book) => {
    return useMemo(() => {
        const types   = Array.isArray(book?.types)    ? book.types    : [];
        const asst    = Array.isArray(book?.asstypes) ? book.asstypes : [];
        const prices  = Array.isArray(book?.prices)   ? book.prices   : [];

        const active = [];
        const upcoming = [];
        const add = (arr, v) => arr.push(v);
        const push = (arr, base) =>
            add(arr, { id: `${base.category}-${base.label}-${base.type}-${base.start}-${base.end}`, ...base });

        const nowM = nowMin();

        const handle = (category, label, discountV, bonusV, ds, de, bs, be) => {
            if (discountV) {
                if (ds <= nowM && de >= nowM)
                    push(active, { type: "Placement Discount", category, label, value: `-${discountV} V`, start: ds, end: de, timeKind: "end" });
                if (ds > nowM)
                    push(upcoming, { type: "Placement Discount", category, label, value: `-${discountV} V`, start: ds, end: de, timeKind: "start" });
            }
            if (bonusV) {
                if (bs <= nowM && be >= nowM)
                    push(active, { type: "Taking Bonus", category, label, value: `+${bonusV} V`, start: bs, end: be, timeKind: "end" });
                if (bs > nowM)
                    push(upcoming, { type: "Taking Bonus", category, label, value: `+${bonusV} V`, start: bs, end: be, timeKind: "start" });
            }
        };

        types.forEach(t =>
            handle("Book Type", t?.type_name,
                t?.book_type_promotion_price_discount,
                t?.book_type_promotion_price_bonus,
                t?.book_type_promotion_price_discount_start_date,
                t?.book_type_promotion_price_discount_end_date,
                t?.book_type_promotion_price_bonus_start_date,
                t?.book_type_promotion_price_bonus_end_date)
        );
        asst.forEach(a =>
            handle("Assignment Type", a?.ass_type_name,
                a?.promotion_ass_price_discount,
                a?.promotion_ass_price_bonus,
                a?.promotion_ass_price_discount_start_date,
                a?.promotion_ass_price_discount_end_date,
                a?.promotion_ass_price_bonus_start_date,
                a?.promotion_ass_price_bonus_end_date)
        );
        prices.forEach(p =>
            handle("Price Type", p?.price,
                p?.promotion_price_discount,
                p?.promotion_price_bonus,
                p?.promotion_price_discount_start_date,
                p?.promotion_price_discount_end_date,
                p?.promotion_price_bonus_start_date,
                p?.promotion_price_bonus_end_date)
        );

        active.sort((a, b) => (a.end ?? 0) - (b.end ?? 0));
        upcoming.sort((a, b) => (a.start ?? 0) - (b.start ?? 0));

        return { active, upcoming };
    }, [book?.types, book?.asstypes, book?.prices]);
};

// plain-text для title
const sentenceTextForOffer = (r) => {
    const isBonus = r?.type === "Taking Bonus";
    const kind = isBonus ? "Bonus" : "Discount";
    const verb = isBonus ? "you take" : "you place";
    if (r?.category === "Assignment Type") return `${kind} is granted when ${verb} an Assignment of type ${r?.label} ${r?.value || ""}`.trim();
    if (r?.category === "Book Type")       return `${kind} is applied when ${verb} an Assignment for Book Type ${r?.label} ${r?.value || ""}`.trim();
    if (r?.category === "Price Type")      return `${kind} is applied when ${verb} an Assignment at price ${r?.label} ${r?.value || ""}`.trim();
    return `${kind} is applied when ${verb} an Assignment ${r?.label ? `(${r.label}) ` : ""}${r?.value || ""}`.trim();
};

// JSX с раскраской
// JSX с раскраской
const sentenceForOffer = (r) => {
    const isBonus = r?.type === "Taking Bonus";
    const kindWord = isBonus ? "Bonus" : "Discount";
    const kindClass = isBonus ? "text-success fw-semibold" : "text-danger fw-semibold";
    const labelClass = kindClass;
    const verb = isBonus ? "you take" : "you place";

    if (r?.category === "Assignment Type") {
        return (
            <>
                <span className={kindClass}>{kindWord}</span>{" "}
                is granted when {verb} an Assignment of type{" "}
                <span className={labelClass}>{r?.label}</span>{" "}
                {r?.value}
            </>
        );
    }
    if (r?.category === "Book Type") {
        return (
            <>
                <span className={kindClass}>{kindWord}</span>{" "}
                is applied when {verb} an Assignment for Book Type{" "}
                <span className={labelClass}>{r?.label}</span>{" "}
                {r?.value}
            </>
        );
    }
    if (r?.category === "Price Type") {
        return (
            <>
                <span className={kindClass}>{kindWord}</span>{" "}
                is applied when {verb} an Assignment at price{" "}
                <span className={labelClass}>{r?.label}</span>{" "}
                {r?.value}
            </>
        );
    }
    return (
        <>
            <span className={kindClass}>{kindWord}</span>{" "}
            is applied when {verb} an Assignment{" "}
            {r?.label ? (<><span className={labelClass}>({r.label})</span>{" "}</>) : null}
            {r?.value}
        </>
    );
};


const DiscountBonusOffers = () => {
    const { book } = useContext(Context);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [snap, setSnap] = useState({ active: [], upcoming: [] });

    const data = useOffers(book);

    useEffect(() => {
        try {
            setLoading(true);
            setSnap(data);
            setErr(null);
        } catch (e) {
            setErr(e?.message || "Failed to prepare offers");
        } finally {
            setLoading(false);
        }
    }, [data]);

    const nothingToShow = !loading &&
        (!snap.active?.length && !snap.upcoming?.length);

    if (nothingToShow) return null;

    return (
        <Card className="p-3 w-100 mt-2">
            <div className="d-flex flex-column flex-md-row flex-md-nowrap justify-content-between align-items-start align-items-md-center gap-2">

            </div>

            {err && <div className="alert alert-danger small mt-2 mb-0">{String(err)}</div>}

            {loading ? (
                <div className="text-center py-3 text-muted small">
                    <Spinner animation="border" size="sm" /> Loading offers…
                </div>
            ) : (
                <div className="mt-0">
                    <Card className="h-100 border-secondary">
                        <Card.Header className="py-1 d-flex align-items-center justify-content-between">
                            <span>Current &amp; Upcoming Offers</span>
                        </Card.Header>
                        <Card.Body className="p-2">
                            {/* Active */}
                            {snap.active?.length > 0 && (
                                <>
                                    <div className="mb-0 small text-uppercase text-muted">Active Offers</div>
                                    <div className="mb-0 ms-1">
                                        {snap.active.map((r, idx) => (
                                            <div
                                                key={`active-${idx}`}
                                                className="d-flex align-items-baseline small"
                                                title={sentenceTextForOffer(r)}
                                            >
                                                <div className="d-flex align-items-center flex-grow-1 me-2" style={{ minWidth: 0 }}>
                                                    <FaHourglassHalf
                                                        className="text-success me-1 animate__animated animate__rotateIn animate__infinite"
                                                        size={10}
                                                    />
                                                    <span className="text-truncate text-nowrap">{sentenceForOffer(r)}</span>
                                                </div>
                                                <div className="flex-grow-1 mx-1 leader-dots"></div>
                                                <div className="ms-2 small text-nowrap text-muted">
                                                    {fmtTimeLeft(r.end, "end")}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {/* Upcoming */}
                            {snap.upcoming?.length > 0 && (
                                <>
                                    {snap.active?.length > 0 ? <hr className="my-2" /> : null}
                                    <div className="mb-0 small text-uppercase text-muted">Upcoming Offers</div>
                                    <div className="mb-0 ms-1">
                                        {snap.upcoming.map((r, idx) => (
                                            <div
                                                key={`upcoming-${idx}`}
                                                className="d-flex align-items-baseline small"
                                                title={sentenceTextForOffer(r)}
                                            >
                                                <div className="d-flex align-items-center flex-grow-1 me-2" style={{ minWidth: 0 }}>
                                                    <FaClock className="text-success me-1" size={10} />
                                                    <span className="text-truncate text-nowrap">{sentenceForOffer(r)}</span>
                                                </div>
                                                <div className="flex-grow-1 mx-1 leader-dots"></div>
                                                <div className="ms-2 small text-nowrap text-muted">
                                                    {fmtTimeLeft(r.start, "start")}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                        </Card.Body>
                    </Card>
                </div>
            )}
        </Card>
    );
};

export default DiscountBonusOffers;
